import { OnboardingMode, TOnboardingAnswers } from "./OnboardingRules";

export type TOnboardingProgress = {
  mode: OnboardingMode;
  email: string;
  current: string;
  history: string[];
  answers: TOnboardingAnswers;
};

const KEY = "family-onboarding-progress";

/**
 * Where the visitor is in the onboarding, kept for the tab so a reload lands on the same
 * screen with the same answers. The password and the PIN are never kept; the rest goes once
 * the family exists.
 */
export default class OnboardingProgress {
  constructor(
    private readonly storage: Storage | null = sessionStorageOrNull(),
  ) {}

  /** The progress of this very visit: same mode, same email. Anything else is ignored. */
  public Load(mode: OnboardingMode, email: string): TOnboardingProgress | null {
    try {
      const raw = this.storage?.getItem(KEY);
      if (!raw) {
        return null;
      }
      const saved = JSON.parse(raw) as TOnboardingProgress;
      if (saved.mode !== mode || saved.email !== email) {
        return null;
      }
      return saved;
    } catch {
      return null;
    }
  }

  public Save(progress: TOnboardingProgress): void {
    try {
      this.storage?.setItem(
        KEY,
        JSON.stringify({
          ...progress,
          answers: {
            ...progress.answers,
            password: "",
            passwordConfirm: "",
            pin: "",
          },
        }),
      );
    } catch {
      // A tab without storage just starts over on reload
    }
  }

  public Clear(): void {
    try {
      this.storage?.removeItem(KEY);
    } catch {
      // Nothing was kept
    }
  }
}

/** The tab's storage, or null where the browser refuses it (private windows, blocked data). */
export function sessionStorageOrNull(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
